<template>
    <Teleport to="body">
        <Transition name="lc-confirm">
            <div
                v-if="dialogState.open"
                class="lc-confirm"
                @mousedown.self="onBackdrop"
            >
                <div
                    class="lc-confirm__dialog"
                    role="alertdialog"
                    aria-modal="true"
                    :aria-label="dialogState.message"
                >
                    <p class="lc-confirm__message">
                        {{ dialogState.message }}
                    </p>
                    <div class="lc-confirm__actions">
                        <button
                            type="button"
                            class="lc-confirm__btn lc-confirm__btn--cancel"
                            :disabled="dialogState.busy"
                            @click="cancelDialog"
                        >
                            {{ dialogState.cancelText }}
                        </button>
                        <button
                            ref="okButton"
                            type="button"
                            class="lc-confirm__btn lc-confirm__btn--ok"
                            :disabled="dialogState.busy"
                            @click="acceptDialog"
                        >
                            <span
                                v-if="dialogState.busy"
                                class="lc-confirm__spinner"
                            />
                            {{ dialogState.okText }}
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
    import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
    import { dialogState, acceptDialog, cancelDialog } from './confirmDialog'

    const okButton = ref(null)
    let lastFocused = null

    const onBackdrop = () => {
        if (dialogState.backdropClose && !dialogState.busy) cancelDialog()
    }

    const onKeydown = (event) => {
        if (!dialogState.open) return
        if (event.key === 'Escape') {
            event.stopPropagation()
            if (!dialogState.busy) cancelDialog()
        } else if (event.key === 'Enter') {
            acceptDialog()
        }
    }

    onMounted(() => window.addEventListener('keydown', onKeydown, true))
    onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown, true))

    watch(() => dialogState.open, async (open) => {
        if (open) {
            lastFocused = document.activeElement
            await nextTick()
            okButton.value?.focus()
        } else {
            lastFocused?.focus?.()
            lastFocused = null
        }
    })
</script>

<style lang="scss" scoped>
    .lc-confirm {
        --lc-confirm-z: 2000;
        --lc-confirm-backdrop: rgba(27, 37, 58, 0.5);
        --lc-confirm-bg: #ffffff;
        --lc-confirm-text: #1b253a;
        --lc-confirm-primary: var(--main-color, #5265e8);

        position: fixed;
        inset: 0;
        z-index: var(--lc-confirm-z);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        background: var(--lc-confirm-backdrop);

        &__dialog {
            width: 100%;
            max-width: 420px;
            padding: 24px;
            background: var(--lc-confirm-bg);
            border-radius: 8px;
            box-shadow: 0 12px 40px -8px rgba(20, 30, 60, 0.35);
        }

        &__message {
            margin: 0;
            font-size: 15px;
            line-height: 1.5;
            color: var(--lc-confirm-text);
            white-space: pre-line;
        }

        &__actions {
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            margin-top: 22px;
        }

        &__btn {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 8px 16px;
            border: 0;
            border-radius: 6px;
            font-size: 14px;
            cursor: pointer;
            transition: filter .15s ease, background-color .15s ease;

            &:disabled {
                opacity: .6;
                cursor: default;
            }

            &--cancel {
                background: #eef1f6;
                color: var(--lc-confirm-text);

                &:hover:not(:disabled) {
                    background: #e3e8f0;
                }
            }

            &--ok {
                background: var(--lc-confirm-primary);
                color: #ffffff;

                &:hover:not(:disabled) {
                    filter: brightness(1.05);
                }

                &:focus-visible {
                    outline: 2px solid var(--lc-confirm-primary);
                    outline-offset: 2px;
                }
            }
        }

        &__spinner {
            width: 14px;
            height: 14px;
            border: 2px solid rgba(255, 255, 255, 0.45);
            border-top-color: #ffffff;
            border-radius: 50%;
            animation: lc-confirm-spin .7s linear infinite;
        }
    }

    .lc-confirm-enter-active,
    .lc-confirm-leave-active {
        transition: opacity .15s linear;

        .lc-confirm__dialog {
            transition: transform .18s ease-out;
        }
    }

    .lc-confirm-enter-from,
    .lc-confirm-leave-to {
        opacity: 0;

        .lc-confirm__dialog {
            transform: translateY(-16px) scale(.98);
        }
    }

    @keyframes lc-confirm-spin {
        to {
            transform: rotate(360deg);
        }
    }
</style>
